import React from 'react';
import {
    Checkbox,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    useTheme,
} from '@fluentui/react';
import { toast } from 'react-toastify';
import { IStorefrontAssetScanResult } from '../../../models/Storefront';
import {
    useCleanupStorefrontAssetOrphansMutation,
    useScanStorefrontAssetsMutation,
} from '../../../store/endpoints/StorefrontAssetEndpoints';
import { blobPathTail, extractErrorMessage, formatBytes } from './assetFormatting';

interface IScanAssetsDialogProps {
    isOpen: boolean;
    onDismiss: () => void;
}

// Reconciles blob storage against the asset library. The scan itself is safe (it only registers
// what it finds); the orphan cleanup is not, so it sits behind its own inline confirm.
const ScanAssetsDialog: React.FC<IScanAssetsDialogProps> = (props) => {
    const theme = useTheme();
    const [scanAssets] = useScanStorefrontAssetsMutation();
    const [cleanupOrphans] = useCleanupStorefrontAssetOrphansMutation();

    const [scanning, setScanning] = React.useState(false);
    const [deleting, setDeleting] = React.useState(false);
    const [result, setResult] = React.useState<IStorefrontAssetScanResult | undefined>();
    const [selectedPaths, setSelectedPaths] = React.useState<string[]>([]);
    const [confirmDelete, setConfirmDelete] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>();

    React.useEffect(() => {
        if (props.isOpen) {
            setResult(undefined);
            setSelectedPaths([]);
            setConfirmDelete(false);
            setErrorMessage(undefined);
        }
    }, [props.isOpen]);

    const runScan = async () => {
        setScanning(true);
        setErrorMessage(undefined);
        setConfirmDelete(false);
        const toastId = toast.loading('Scanning storefront assets…');
        try {
            const response = await scanAssets().unwrap();
            setResult(response);
            setSelectedPaths([]);
            toast.update(toastId, {
                type: 'success',
                render: `Scan complete: ${response.registered} new asset${response.registered === 1 ? '' : 's'} registered`,
                isLoading: false,
                autoClose: 3000,
            });
        } catch (e) {
            const detail = extractErrorMessage(e);
            setErrorMessage(detail ?? 'Failed to scan assets');
            toast.update(toastId, {
                type: 'error',
                render: `Failed to scan storefront assets${detail ? `: ${detail}` : ''}`,
                isLoading: false,
                autoClose: 4000,
            });
        }
        setScanning(false);
    };

    const deleteSelected = async () => {
        if (selectedPaths.length === 0) return;
        setDeleting(true);
        setConfirmDelete(false);
        setErrorMessage(undefined);
        const count = selectedPaths.length;
        const toastId = toast.loading(`Deleting ${count} orphaned file${count === 1 ? '' : 's'}…`);
        try {
            await cleanupOrphans({ blobPaths: selectedPaths }).unwrap();
            toast.update(toastId, {
                type: 'success',
                render: `${count} orphaned file${count === 1 ? '' : 's'} deleted`,
                isLoading: false,
                autoClose: 3000,
            });
            setResult(result
                ? { ...result, orphans: result.orphans.filter(o => !selectedPaths.includes(o.blobPath)) }
                : result);
            setSelectedPaths([]);
        } catch (e) {
            const detail = extractErrorMessage(e);
            setErrorMessage(detail ?? 'Failed to delete orphaned files');
            toast.update(toastId, {
                type: 'error',
                render: `Failed to delete orphaned files${detail ? `: ${detail}` : ''}`,
                isLoading: false,
                autoClose: 4000,
            });
        }
        setDeleting(false);
    };

    const togglePath = (blobPath: string, checked?: boolean) => {
        setSelectedPaths(checked
            ? [...selectedPaths, blobPath]
            : selectedPaths.filter(p => p !== blobPath));
    };

    const orphans = result?.orphans ?? [];

    return (
        <Dialog
            hidden={!props.isOpen}
            onDismiss={props.onDismiss}
            minWidth={560}
            dialogContentProps={{
                type: DialogType.largeHeader,
                title: 'Scan for Assets',
                subText: 'Compares storage with the asset library. Registers images that are missing from the library, and lists orphaned files that are no longer referenced anywhere.',
            }}
            modalProps={{ isBlocking: true }}
        >
            <Stack tokens={{ childrenGap: '1em' }}>
                {errorMessage && (
                    <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setErrorMessage(undefined)}>
                        {errorMessage}
                    </MessageBar>
                )}

                <Stack horizontal verticalAlign='center' tokens={{ childrenGap: '1em' }}>
                    <PrimaryButton
                        text={result ? 'Scan Again' : 'Scan'}
                        iconProps={{ iconName: 'SearchAndApps' }}
                        disabled={scanning || deleting}
                        onClick={runScan}
                    />
                    {scanning && <Spinner size={SpinnerSize.small} label='Scanning' labelPosition='right' />}
                </Stack>

                {result && (
                    <MessageBar messageBarType={MessageBarType.success}>
                        {result.registered} new asset{result.registered === 1 ? '' : 's'} registered,{' '}
                        {result.alreadyRegistered} already known, {result.legacyUrls} legacy URL{result.legacyUrls === 1 ? '' : 's'}.
                    </MessageBar>
                )}

                {result && orphans.length === 0 && (
                    <div>No orphaned files in storage.</div>
                )}

                {orphans.length > 0 && (
                    <div>
                        <div style={{ fontWeight: 600, marginBottom: '0.5em' }}>
                            Orphaned files ({orphans.length}): in storage but not referenced anywhere
                        </div>
                        <div style={{ maxHeight: 240, overflowY: 'auto', border: `1px solid ${theme.palette.neutralLight}` }}>
                            {orphans.map(orphan => (
                                <div
                                    key={orphan.blobPath}
                                    style={{ display: 'flex', alignItems: 'center', gap: '0.5em', padding: '0.25em 0.5em' }}
                                >
                                    <Checkbox
                                        checked={selectedPaths.includes(orphan.blobPath)}
                                        disabled={deleting}
                                        onChange={(_, checked) => togglePath(orphan.blobPath, checked)}
                                    />
                                    <div style={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        <span title={orphan.blobPath}>{blobPathTail(orphan.blobPath)}</span>
                                    </div>
                                    <div style={{ color: theme.palette.neutralSecondary, fontSize: 12, whiteSpace: 'nowrap' }}>
                                        {formatBytes(orphan.sizeBytes)}
                                        {orphan.lastModified ? ` · ${new Date(orphan.lastModified).toLocaleDateString()}` : ''}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* Inline confirm rather than a second Dialog; layered modals in Fluent v8
                            are fragile, and the destructive step stays in context here. */}
                        {confirmDelete ? (
                            <MessageBar
                                messageBarType={MessageBarType.severeWarning}
                                actions={
                                    <div>
                                        <PrimaryButton
                                            text='Delete'
                                            styles={{ root: { backgroundColor: theme.palette.red, borderColor: theme.palette.red } }}
                                            disabled={deleting}
                                            onClick={deleteSelected}
                                        />
                                        <DefaultButton text='Cancel' disabled={deleting} onClick={() => setConfirmDelete(false)} />
                                    </div>
                                }
                            >
                                Permanently deletes {selectedPaths.length} file{selectedPaths.length === 1 ? '' : 's'} from storage.
                            </MessageBar>
                        ) : (
                            <div style={{ marginTop: '0.5em' }}>
                                <DefaultButton
                                    text={`Delete selected${selectedPaths.length ? ` (${selectedPaths.length})` : ''}`}
                                    iconProps={{ iconName: 'Delete' }}
                                    disabled={selectedPaths.length === 0 || deleting}
                                    onClick={() => setConfirmDelete(true)}
                                />
                            </div>
                        )}
                    </div>
                )}
            </Stack>

            <DialogFooter>
                <DefaultButton text='Close' disabled={deleting} onClick={props.onDismiss} />
            </DialogFooter>
        </Dialog>
    );
};

export default ScanAssetsDialog;
